import React, { useState } from "react";
import { ArrowLeft, Check, Eye, EyeOff } from "lucide-react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import { resetPassword } from "@/api/auth";
import NativeAppReturnLink from "@/components/native/NativeAppReturnLink";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { isNativeApp } from "@/lib/runtime";
import { isAndroidWebBrowser } from "@/lib/nativeAppLinks";
import PasswordRequirements from "@/components/PasswordRequirements";
import { isPasswordValid, PASSWORD_POLICY_MESSAGE } from "@/lib/passwordPolicy";

function PasswordField({ id, label, value, onChange, native }) {
  const [visible, setVisible] = useState(false);
  return (
    <div>
      <Label htmlFor={id}>{label}</Label>
      <div className="relative">
        <Input
          id={id}
          type={visible ? "text" : "password"}
          autoComplete="new-password"
          value={value}
          onChange={onChange}
          required
        />
        {native ? (
          <button
            type="button"
            className="native-v3-password-toggle"
            onClick={() => setVisible((current) => !current)}
            aria-label={visible ? "Dölj lösenord" : "Visa lösenord"}
          >
            {visible ? <EyeOff aria-hidden="true" /> : <Eye aria-hidden="true" />}
          </button>
        ) : null}
      </div>
    </div>
  );
}

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const returnScheme = searchParams.get("return_scheme") || undefined;
  const navigate = useNavigate();
  const native = isNativeApp();

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const onSubmit = async (event) => {
    event.preventDefault();
    if (!token) {
      toast.error("Ogiltig länk", { description: "Saknar token för återställning." });
      return;
    }
    if (!password || password !== confirm) {
      toast.error("Lösenorden matchar inte", {
        description: "Kontrollera att båda fälten är identiska.",
      });
      return;
    }
    if (!isPasswordValid(password)) {
      toast.error("Lösenordet uppfyller inte kraven", { description: PASSWORD_POLICY_MESSAGE });
      return;
    }

    setLoading(true);
    try {
      await resetPassword(token, password);
      setDone(true);
      toast.success("Lösenord uppdaterat", {
        description: "Du kan nu logga in med ditt nya lösenord.",
      });
      if (!isAndroidWebBrowser()) {
        window.setTimeout(() => navigate("/", { replace: true }), 1200);
      }
    } catch (error) {
      toast.error("Kunde inte återställa lösenord", {
        description: error?.message || "Länken kan vara ogiltig eller ha gått ut.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`native-v3-public-flow${native ? "" : " public-auth-v3"}`}>
      <header className="native-v3-public-flow-header">
        <Link to="/auth" aria-label="Tillbaka till inloggning" className="native-v3-public-back">
          <ArrowLeft aria-hidden="true" />
        </Link>
        <div className="native-v3-public-brand">
          <img src="/icons/icon-512x512.png" alt="" aria-hidden="true" />
          <span>Lapstr</span>
        </div>
      </header>

      <main className="native-v3-public-flow-main">
        <div className="native-v3-public-flow-intro">
          <p className="native-v3-auth-kicker">NYTT LÖSENORD</p>
          <h1 className="native-v3-auth-title">Välj nytt lösenord</h1>
          <p className="native-v3-auth-lead">Välj ett nytt lösenord för ditt konto.</p>
        </div>

        {!token ? (
          <div className="native-v3-auth-message">
            <p>Länken saknar en giltig token. Kontrollera att du följde hela adressen från e-postmeddelandet.</p>
            <p>Behöver du en ny länk? <Link to="/auth/forgot-password">Beställ en ny här</Link>.</p>
          </div>
        ) : !done ? (
          <form onSubmit={onSubmit} className="native-v3-auth-form">
            <PasswordField native id="password" label="Nytt lösenord" value={password} onChange={(event) => setPassword(event.target.value)} />
            <PasswordField native id="confirm" label="Bekräfta nytt lösenord" value={confirm} onChange={(event) => setConfirm(event.target.value)} />
            <PasswordRequirements password={password} />
            <Button type="submit" className="w-full" disabled={loading || !isPasswordValid(password) || password !== confirm}>
              {loading ? "Sparar..." : "Spara nytt lösenord"}
            </Button>
          </form>
        ) : (
          <div className="native-v3-auth-sent">
            <Check aria-hidden="true" />
            <h2>Lösenordet är uppdaterat</h2>
            <p>{isAndroidWebBrowser() ? "Öppna Lapstr-appen för att logga in." : "Du skickas automatiskt vidare till inloggningen."}</p>
            <NativeAppReturnLink result="password-reset" returnScheme={returnScheme}>
              Öppna Lapstr och logga in
            </NativeAppReturnLink>
          </div>
        )}

        <div className="native-v3-public-flow-footer">
          <Link to="/auth" className="text-primary font-medium hover:underline">Tillbaka till inloggning</Link>
        </div>
      </main>
    </div>
  );
}
